import { useCallback, useRef, type ChangeEvent } from 'react'
import { useAppDispatch } from '../../../store/hooks/hooks'
import { addNode } from '../../../store/slices/sessionSlice'
import { setNodes as setSelectedNodes } from '../../../store/slices/selectionSlice'
import { appendMessage } from '../../../store/slices/chatSlice'
import { uploadNode } from '../../../services/node'
import type { SessionInfoResponse } from 'models-ts/session'

export function useFileHandler(
    session: SessionInfoResponse | null,
    refreshSession: () => Promise<void>
) {
    const dispatch = useAppDispatch()
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const handlePickFile = useCallback(() => {
        fileInputRef.current?.click()
    }, [])

    const handleFileChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file || !session) return
        try {
            const node = await uploadNode(file, session.id, 'image')
            dispatch(addNode({ sessionId: session.id, node }))
            if (node?.id) {
                dispatch(setSelectedNodes({ sessionId: session.id, nodeIds: [node.id] }))
            }
            refreshSession()
            dispatch(appendMessage({
                sessionId: session.id,
                message: {
                    kind: 'note',
                    text: 'image uploaded',
                    id: crypto.randomUUID(),
                    timestamp: Date.now()
                }
            }))
        } catch (err: any) {
            dispatch(appendMessage({
                sessionId: session.id,
                message: {
                    kind: 'note',
                    text: err?.message ?? 'Upload failed',
                    id: crypto.randomUUID(),
                    timestamp: Date.now()
                }
            }))
        } finally {
            e.target.value = ''
        }
    }, [session, dispatch, refreshSession])

    return {
        fileInputRef,
        handlePickFile,
        handleFileChange
    }
}
